import { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { openModal } from 'mastodon/actions/modal';
import {
  domain,
  limitedFederationMode,
  registrationsOpen,
  signInBanner,
  sso_redirect,
} from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

// Line breaks typed by the admin are kept
const customTextStyle = { whiteSpace: 'pre-line' } as const;

// The admin can replace the wording in Server settings > Appearance
const Title: React.FC = () =>
  signInBanner?.title ? (
    <span style={customTextStyle}>{signInBanner.title}</span>
  ) : (
    <FormattedMessage
      id='sign_in_banner.mastodon_is'
      defaultMessage="Mastodon is the best way to keep up with what's happening."
    />
  );

// A server that does not federate only has its own people to follow
const DefaultFollowAnyone: React.FC = () =>
  limitedFederationMode ? (
    <FormattedMessage
      id='sign_in_banner.follow_anyone_closed'
      defaultMessage='Follow people on {domain} and see it all in chronological order. No algorithms, ads, or clickbait in sight.'
      values={{ domain }}
    />
  ) : (
    <FormattedMessage
      id='sign_in_banner.follow_anyone'
      defaultMessage='Follow anyone across the fediverse and see it all in chronological order. No algorithms, ads, or clickbait in sight.'
    />
  );

const FollowAnyone: React.FC = () =>
  signInBanner?.text ? (
    <span style={customTextStyle}>{signInBanner.text}</span>
  ) : (
    <DefaultFollowAnyone />
  );

export const SignInBanner: React.FC = () => {
  const dispatch = useAppDispatch();

  const openClosedRegistrationsModal = useCallback(
    () =>
      dispatch(
        openModal({ modalType: 'CLOSED_REGISTRATIONS', modalProps: {} }),
      ),
    [dispatch],
  );

  let signupButton: React.ReactNode;

  const signupUrl = useAppSelector(
    (state) => state.server.server.item?.registrations.url ?? '/auth/sign_up',
  );

  if (sso_redirect) {
    return (
      <div className='sign-in-banner'>
        <p>
          <strong>
            <Title />
          </strong>
        </p>
        <p>
          <FollowAnyone />
        </p>
        <a
          href={sso_redirect}
          data-method='post'
          className='button button--block button-secondary'
        >
          <FormattedMessage
            id='sign_in_banner.sso_redirect'
            defaultMessage='Login or Register'
          />
        </a>
      </div>
    );
  }

  if (registrationsOpen) {
    signupButton = (
      <a href={signupUrl} className='button button--block'>
        <FormattedMessage
          id='sign_in_banner.create_account'
          defaultMessage='Create account'
        />
      </a>
    );
  } else {
    signupButton = (
      <button
        className='button button--block'
        onClick={openClosedRegistrationsModal}
        type='button'
      >
        <FormattedMessage
          id='sign_in_banner.create_account'
          defaultMessage='Create account'
        />
      </button>
    );
  }

  return (
    <div className='sign-in-banner'>
      <p>
        <strong>
          <Title />
        </strong>
      </p>
      <p>
        <FollowAnyone />
      </p>
      {signupButton}
      <a href='/auth/sign_in' className='button button--block button-secondary'>
        <FormattedMessage id='sign_in_banner.sign_in' defaultMessage='Login' />
      </a>
    </div>
  );
};
